import { buildMeta } from "@/lib/seo";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Info, AlertTriangle } from "lucide-react";
import { InteractionDetailCard } from "@/components/InteractionDetailCard";
import { getInteractionByPair } from "@/lib/public-matrix.functions";
import { MARKER_META, RISK_META, type InteractionRisk } from "@/types/restlicht-db";
import { riskLabelKey } from "@/lib/risk-labels";
import { toneAlpha } from "@/lib/tone";
import { useT } from "@/i18n/LanguageContext";
import { errorMessage } from "@/lib/error-capture";


const pairQuery = (pair: string) =>
  queryOptions({
    queryKey: ["public-matrix-pair", pair],
    queryFn: () => getInteractionByPair({ data: { pair } }),
  });

export const Route = createFileRoute("/matrix_/$pair")({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData(pairQuery(params.pair));
    if (!data) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    const title = loaderData
      ? `${loaderData.a} + ${loaderData.b} - Interaktion`
      : "Interaktion";
    const desc = loaderData?.interaction?.content_de?.kernaussage
      ?? "Detaillierte Informationen zu einer Substanzkombination.";
    return buildMeta({
      title,
      description: desc,
      path: `/matrix/${params.pair}`,
      type: "article",
    });
  },
  errorComponent: ({ error }) => (
    <div style={{ padding: "2rem" }}>
      <h1 className="page-title">Fehler</h1>
      <div className="page-title-bar" />
      <p style={{ color: "#ef4444" }}>{errorMessage(error)}</p>
      <Link to="/matrix" style={{ color: "var(--accent-cyan)" }}>Zurück zur Matrix</Link>
    </div>
  ),
  notFoundComponent: () => (
    <div style={{ padding: "2rem" }}>
      <h1 className="page-title">Nicht gefunden</h1>
      <div className="page-title-bar" />
      <p style={{ color: "var(--text-muted)" }}>Diese Substanzkombination existiert nicht.</p>
      <Link to="/matrix" style={{ color: "var(--accent-cyan)" }}>Zurück zur Matrix</Link>
    </div>
  ),
  component: PairPage,
});

function PairPage() {
  const { pair } = Route.useParams();
  const fetchPair = useServerFn(getInteractionByPair);
  const { data } = useSuspenseQuery({
    ...pairQuery(pair),
    queryFn: () => fetchPair({ data: { pair } }),
  });

  if (!data) return null;

  return (
    <div style={{ maxWidth: 820, margin: "0 auto" }}>
      <Link to="/matrix" style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        color: "var(--accent-cyan)", textDecoration: "none",
        fontSize: "0.85rem", marginBottom: "1rem",
      }}>
        <ArrowLeft size={14} /> Zurück zur Matrix
      </Link>

      <h1 className="page-title">Interaktion Details</h1>
      <div className="page-title-bar" />

      {data.interaction ? (
        <div style={{ marginTop: "1.5rem" }}>
          <InteractionDetailCard
            aName={data.a}
            bName={data.b}
            riskLevel={data.interaction.risk_level}
            markers={data.interaction.markers}
            content={data.interaction.content_de}
            interactionId={data.interaction.id}
            variant="page"
          />
        </div>
      ) : (
        <div className="glass-card" style={{ padding: "1.5rem", marginTop: "1.5rem", display: "flex", gap: 10, alignItems: "center" }}>
          <Info size={18} style={{ color: "var(--accent-cyan)" }} />
          <span style={{ color: "var(--text-muted)" }}>
            Für {data.a} + {data.b} liegen aktuell keine veröffentlichten Details vor.
          </span>
        </div>
      )}

      <Legend />

      <div className="glass-card" style={{
        padding: "1rem 1.5rem", marginTop: "1.25rem",
        display: "flex", alignItems: "center", gap: "0.6rem",
      }}>
        <Info size={16} style={{ color: "var(--accent-cyan)", flexShrink: 0 }} aria-hidden="true" />
        <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
          Diese Inhalte dienen Informationszwecken. Individuelle Reaktionen können variieren. Kein Ersatz für medizinische Beratung.
        </span>
      </div>
    </div>
  );
}

function Legend() {
  const t = useT();
  const risks: InteractionRisk[] = ["low_risk_synergy", "low_risk_no_synergy", "low_risk_decrease", "caution", "unsafe", "dangerous", "unknown"];
  return (
    <section
      aria-labelledby="legend-heading"
      className="glass-card"
      style={{ padding: "1.25rem 1.5rem", marginTop: "2rem", display: "flex", flexDirection: "column", gap: "1rem" }}
    >
      <h2
        id="legend-heading"
        className="font-display"
        style={{ margin: 0, fontSize: "1rem", color: "var(--accent-cyan)", display: "inline-flex", alignItems: "center", gap: 8 }}
      >
        <AlertTriangle size={16} aria-hidden="true" /> Legende
      </h2>
      <div>
        <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: 8 }}>
          Risikostufen
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {risks.map((r) => {
            const m = RISK_META[r];
            const label = t(riskLabelKey(r));
            return (
              <li key={r}>
                <span
                  role="img"
                  aria-label={label}
                  title={label}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "0.3rem 0.7rem", borderRadius: 14,
                    background: toneAlpha(m.tone, 12), color: m.tone,
                    border: `1px solid ${toneAlpha(m.tone, 40)}`,
                    fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em",
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: m.tone, display: "inline-block" }} aria-hidden="true" />
                  {label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
      <div>
        <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: 8 }}>
          Marker
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {Object.entries(MARKER_META).map(([key, m]) => (
            <li
              key={key}
              style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: "0.85rem", color: "var(--text-primary)" }}
            >
              <span
                role="img"
                aria-label={m.label}
                title={m.description}
                style={{
                  flexShrink: 0, padding: "0.2rem 0.55rem", borderRadius: 10,
                  background: toneAlpha(m.tone, 13), border: `1px solid ${toneAlpha(m.tone, 53)}`,
                  color: m.tone, fontSize: "0.68rem", fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: "0.04em",
                  minWidth: 130, textAlign: "center",
                }}
              >
                {m.label}
              </span>
              <span style={{ color: "var(--text-muted)", lineHeight: 1.5 }}>{m.description}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

